import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Schema } from '../../../../../../amplify/data/resource';
import SelectTransparent from '../../../../../components/custom/SelectTransparent';
import TextFieldUnderline from '../../../../../components/custom/TextFieldUnderline';

interface Props {
  open: boolean;
  onClose: () => void;
  contactId?: string;
  userId: string;
}

interface FormValues {
  type: string;
  fullName: string;
  phone: string;
  email: string;
  notes: string;
}

const apiClient = generateClient<Schema>();

const EditContact: React.FC<Props> = ({ open, onClose, contactId, userId }) => {
  const theme = useTheme();

  const handleClose = () => {
    onClose();
  };

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    setError,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      type: '',
      fullName: '',
    },
  });

  useEffect(() => {
    const fetchContact = async () => {
      if (!contactId) return;

      try {
        const response = await apiClient.models.Contact.get({ id: contactId });

        if (response?.data) {
          setValue('type', response.data.type || '');
          setValue('fullName', response.data.fullName || '');
          setValue('phone', response.data.phone || '');
          setValue('email', response.data.email || '');
          setValue('notes', response.data.notes || '');
        }
      } catch (error) {
        console.error('Failed to fetch contact:', error);
      }
    };

    fetchContact();
  }, [contactId, setValue]);

  const onSubmit = async (formData: FormValues) => {
    try {
      if (contactId) {
        await apiClient.models.Contact.update({
          id: contactId,
          ...formData,
        });
        console.log('Contact updated successfully');
      } else {
        await apiClient.models.Contact.create({
          ...formData,
          userId: userId,
        });
        console.log('Contact created successfully');
      }

      reset();
      onClose();
    } catch (error) {
      console.error('Failed to submit contact:', error);
    }
  };

  return (
    <Dialog
      className="edit-dialog"
      fullScreen
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          backgroundColor: '#E7ECF1',
          borderRadius: { xs: '0', sm: '16px' },
          padding: { xs: '20px', sm: '40px', md: '60px' },
          boxSizing: 'border-box',
          position: 'relative',
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: { xs: '100%', md: '350px' },
          height: '100%',
          backgroundImage: 'url(/assets/client/background.svg)',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'left center',
          opacity: 0.5,
          zIndex: 0,
        }}
      />

      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          padding: { xs: '10px', sm: '20px' },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            width: '50px',
            height: '50px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 0,
          }}
        >
          <img
            alt="close"
            src={'/assets/client/close.svg'}
            width="35"
            height="35"
            style={{ cursor: 'pointer' }}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          gap: '24px',
          width: '100%',
          px: { xs: '16px', sm: '32px', md: '60px' },
          pb: { xs: '16px', sm: '32px' },
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '1200px',
            mx: 'auto',
          }}
        >
          <DialogContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              pt: '10px',
              px: '100px',
              backgroundColor: 'transparent',
              flex: 1,
              overflowY: 'auto',
              boxSizing: 'border-box',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Stack
              component="form"
              direction="row"
              justifyContent={'space-between'}
              onSubmit={handleSubmit(onSubmit)}
            >
              <Stack
                spacing="40px"
                sx={{
                  background: 'transparent',
                  zIndex: 2,
                  width: '400px',
                  justifyContent: 'center',
                }}
              >
                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextFieldUnderline
                      {...field}
                      label="הערות"
                      fullWidth
                      multiline
                      minRows={5}
                      error={!!errors.notes}
                      helperText={errors.notes?.message}
                    />
                  )}
                />
                <Button
                  type="submit"
                  sx={{
                    backgroundColor: '#B6F829',
                    borderRadius: '15px',
                    ':hover': { backgroundColor: '#B6F829' },
                  }}
                >
                  <Typography
                    fontSize="32px"
                    fontWeight="700"
                    color="black"
                    fontFamily={'AlmoniDL'}
                  >
                    שמירה
                  </Typography>
                </Button>
              </Stack>
              <Stack spacing={5}>
                <Typography
                  fontSize="60px"
                  fontWeight="300"
                  fontFamily={'AlmoniDL'}
                >
                  הוספת בעל מקצוע
                </Typography>
                <Stack spacing={10}>
                  <Controller
                    name="type"
                    control={control}
                    rules={{ required: 'חובה למלא את השדה' }}
                    render={({ field }) => (
                      <SelectTransparent
                        {...field}
                        id={field.name}
                        label="תחום עיסוק"
                        error={!!errors.type}
                        helperText={errors.type?.message}
                        options={[
                          { label: 'סוכן ביטוח', value: 'סוכן ביטוח' },
                          { label: 'רואה חשבון', value: 'רואה חשבון' },
                          { label: 'אחר', value: 'אחר' },
                        ]}
                      />
                    )}
                  />
                  <Controller
                    name="fullName"
                    control={control}
                    rules={{ required: 'חובה למלא את השדה' }}
                    render={({ field }) => (
                      <TextFieldUnderline
                        {...field}
                        label="שם"
                        fullWidth
                        required
                        error={!!errors.fullName}
                        helperText={errors.fullName?.message}
                      />
                    )}
                  />
                  <Controller
                    name="phone"
                    control={control}
                    rules={{ required: 'חובה למלא את השדה' }}
                    render={({ field }) => (
                      <TextFieldUnderline
                        {...field}
                        label="טלפון"
                        fullWidth
                        required
                        error={!!errors.phone}
                        helperText={errors.phone?.message}
                      />
                    )}
                  />

                  <Controller
                    name="email"
                    control={control}
                    rules={{ required: 'חובה למלא את השדה' }}
                    render={({ field }) => (
                      <TextFieldUnderline
                        {...field}
                        label="מייל"
                        fullWidth
                        multiline
                        required
                        error={!!errors.email}
                        helperText={errors.email?.message}
                      />
                    )}
                  />
                </Stack>
              </Stack>
            </Stack>
          </DialogContent>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EditContact;
